import React from 'react';
import { useTranslation } from 'react-i18next';
import { CloudServerOutlined } from '@ant-design/icons';
import { VMConfig } from '../../../types/vm.type';
import { OperatingSystemOption } from '../../../types/vm.type';
import { vmInstanceTypes } from '../../../constants/vmConstants';

interface Step3VMProps {
  config: VMConfig;
  operatingSystems: OperatingSystemOption[];
}

const Step3VM: React.FC<Step3VMProps> = ({ config, operatingSystems }) => {
  const { t } = useTranslation();

  const getEstimatedCost = () => {
    if (!config.instanceType) return '$0.00';
    const instance = vmInstanceTypes.find((i) => i.id === config.instanceType);
    if (!instance) return '$0.00';
    const hourlyRate = parseFloat(instance.price.replace('$', '').replace('/hr', ''));
    const monthlyRate = (hourlyRate * 730).toFixed(2);
    return `$${monthlyRate}`;
  };

  const selectedOSName = operatingSystems.find((os) => os.id === config.os)?.name;
  const selectedInstance = vmInstanceTypes.find((i) => i.id === config.instanceType);

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 dark:bg-dark-bg rounded-lg p-6">
        <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CloudServerOutlined className="text-xl" />
          {t('virtualMachines.create.step3.review')}
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.name')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.os')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{selectedOSName}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.instanceType')}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {selectedInstance ? (
                <>
                  <span className="font-mono">{selectedInstance.kubiyName}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({selectedInstance.awsType})</span>
                </>
              ) : (
                config.instanceType
              )}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.storage')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.storage} GB</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.backups')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.backups ? t('common.yes') : t('common.no')}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step1.monitoring')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.monitoring ? t('common.yes') : t('common.no')}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step3.publicIP')}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.needsPublicIP ? t('common.yes') : t('common.no')}</span>
          </div>
          {config.needsPublicIP && (
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600 pl-4">
              <span className="text-gray-600 dark:text-gray-400">{t('virtualMachines.create.step3.ipType')}</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {config.publicIPType === 'dynamic' ? t('virtualMachines.create.step2.dynamic') : t('virtualMachines.create.step2.elastic')}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-gray-900 dark:text-white">Estimated Monthly Cost</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{getEstimatedCost()}</p>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Based on 730 hours/month. Actual costs may vary based on usage.
        </p>
      </div>
    </div>
  );
};

export default Step3VM;
